import { ApiError, ErrorCode } from '@approvalmax/types';
import { errorHelpers } from '@approvalmax/utils';
import { SsoData } from 'app/(workspace)/profileSetup/sso';
import { actions, selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { integrationActions } from 'modules/integration';
import { Dispatch } from 'modules/react-redux';
import { SignInPostAction, SignInPostActionType, SignInUiState } from 'pages/auth/helpers';
import { CompanyRequestListFilter } from 'pages/requestList/config';
import { api } from 'services/api';
import { routingService } from 'services/routing';
import { storageService } from 'services/storage';
import { setWrongEmailSso } from 'shared/helpers';
import { jotaiStore } from 'shared/store';
import { AccountPath, getAccountUrl, getDefaultPath, getPartnerUrl, getPath, getUrl, Path } from 'urlBuilder';

import { showQbooksVerifyEmailPopupState } from '../states/showQbooksVerifyEmailPopupState';
import { LoadSsoStep2SequenceOptions } from '../types';

/**
 * Request to the required OAuth provider
 */
const requestOAuthLogin = async (options: LoadSsoStep2SequenceOptions) => {
    const params = {
        code: options.code,
        state: options.state,
        emailAddress: options.allowedEmail,
    };

    // TODO: Should be moved to the react-query hooks in the future
    switch (options.oauthProvider) {
        case domain.OAuthProvider.Xero:
            return api.auth.loginViaXeroOAuth(params);
        case domain.OAuthProvider.QBooks:
            return api.auth.loginViaQBooksOAuth(params);
        case domain.OAuthProvider.Google:
            return api.auth.loginViaGoogleOAuth(params);
        case domain.OAuthProvider.Microsoft:
            return api.auth.loginViaMicrosoftOAuth(params);
        default:
            throw errorHelpers.assertNever(options.oauthProvider);
    }
};

/**
 * Handling authorization error
 */
const handleAuthError = (err: ApiError, options: LoadSsoStep2SequenceOptions) => {
    if (err.code === ErrorCode.E3002_EMAIL_NOT_VERIFIED && options.oauthProvider === domain.OAuthProvider.QBooks) {
        jotaiStore.set(showQbooksVerifyEmailPopupState, true);
        routingService.push(getPath(Path.login));

        return;
    }

    if (err.code === ErrorCode.E3010_EMAIL_NOT_MATCH_SSO) {
        storageService.setAuthenticated(true);
        setWrongEmailSso();
        routingService.reloadToUrl(getPath(Path.profileSetupEmail));

        return;
    }

    routingService.pushToDefaultPath();
};

/**
 * Handling postAction = Connect
 */
const handleConnectPostAction = async (
    postAction: Extract<SignInPostAction, { type: SignInPostActionType.Connect }>,
    options: LoadSsoStep2SequenceOptions,
    dispatch: Dispatch,
    getState: () => State
) => {
    switch (options.oauthProvider) {
        case domain.OAuthProvider.Xero:
        case domain.OAuthProvider.Google:
        case domain.OAuthProvider.Microsoft:
            throw errorHelpers.notSupportedError();

        case domain.OAuthProvider.QBooks: {
            if (postAction.externalOrgId) {
                // TODO: Should be moved to the react-query hooks in the future
                const context = await api.companies.getUserContext({});

                dispatch(actions.loadInitialAppData({ context }));

                const state = getState();
                const integration = selectors.integration
                    .getConnectedIntegrations(state)
                    .find(({ integratedCompanyId }) => integratedCompanyId === postAction.externalOrgId);

                if (integration) {
                    routingService.replaceToDefaultPath();

                    return;
                }
            }

            const response = await integrationActions.qbooksIntegration.connectToIntegration({
                callbackUrl: getUrl(Path.signInWithStartFinalizeQBooks),
            });

            routingService.navigateToExternalUrl(response.redirectUrl);

            return;
        }

        default:
            throw errorHelpers.assertNever(options.oauthProvider);
    }
};

/**
 * Handling postAction = Disconnect
 */
const handleDisconnectPostAction = async (
    postAction: Extract<SignInPostAction, { type: SignInPostActionType.Disconnect }>,
    dispatch: Dispatch,
    getState: () => State
) => {
    // TODO: Should be moved to the react-query hooks in the future
    const context = await api.companies.getUserContext({});

    dispatch(actions.loadInitialAppData({ context }));

    const state = getState();
    const integration = selectors.integration
        .getConnectedIntegrations(state)
        .find(({ integratedCompanyId }) => integratedCompanyId === postAction.externalOrgId);

    if (!integration) {
        routingService.pushToDefaultPath();

        return;
    }

    const companyId = integration.companyId;

    dispatch(integrationActions.disconnectFromIntegration(companyId));
    routingService.replace(getPath(Path.companyWorkflows, companyId));
};

/**
 * Handling postAction
 */
const handlePostAction = async (
    postAction: SignInPostAction,
    options: LoadSsoStep2SequenceOptions,
    dispatch: Dispatch,
    getState: () => State
) => {
    switch (postAction.type) {
        case SignInPostActionType.Connect:
            await handleConnectPostAction(postAction, options, dispatch, getState);

            return;

        case SignInPostActionType.Disconnect:
            await handleDisconnectPostAction(postAction, dispatch, getState);

            return;

        default:
            throw errorHelpers.assertNever(postAction);
    }
};

/**
 * Handling a scenario when the user is already logged in
 */
const handleLoggedInUser = async (
    uiState: SignInUiState,
    options: LoadSsoStep2SequenceOptions,
    dispatch: Dispatch,
    getState: () => State
) => {
    storageService.setAuthenticated(true);

    let nextUrl: string | undefined;

    const { app } = uiState;

    switch (app) {
        case domain.SsoLoginApplication.Account:
            nextUrl = getAccountUrl(AccountPath.root);
            break;
        case domain.SsoLoginApplication.Partner:
            nextUrl = getPartnerUrl();
            break;
        case undefined:
            break;
        default:
            throw errorHelpers.assertNever(app);
    }

    if (nextUrl) {
        if (uiState.afterLoginRedirectUrl) {
            const suffix = uiState.afterLoginRedirectUrl.startsWith('/')
                ? uiState.afterLoginRedirectUrl
                : `/${uiState.afterLoginRedirectUrl}`;

            nextUrl += suffix;
        }

        routingService.navigateToExternalUrl(nextUrl);

        return;
    }

    if (uiState.postAction) {
        await handlePostAction(uiState.postAction, options, dispatch, getState);

        return;
    }

    if (uiState.tenantId) {
        // TODO: Should be moved to the react-query hooks in the future
        const context = await api.companies.getUserContext({});

        dispatch(actions.loadInitialAppData({ context }));

        const state = getState();
        const integration = selectors.integration
            .getIntegrations(state)
            .find((x) => x.integratedCompanyId === uiState.tenantId);

        if (integration?.companyId) {
            routingService.replace(
                getPath(Path.requestCompanyList, integration.companyId, CompanyRequestListFilter.CompanyAll)
            );
        } else {
            routingService.replace(getDefaultPath());
        }
    } else {
        routingService.reloadToUrl(uiState.afterLoginRedirectUrl || getDefaultPath());
    }
};

/**
 * Handling a scenario when the user is not logged in yet
 */
const handleNotLoggedInUser = (
    setSsoData: (data: SsoData | null) => void,
    oauthProvider: domain.OAuthProvider,
    uiState: SignInUiState,
    params: {
        firstName: string;
        lastName: string;
        email: string;
        signUpCode: string;
    }
) => {
    setSsoData({
        ...params,
        oauthProvider,
        app: uiState.app,
        startingPoint: uiState.startingPoint,
        redirectUrl: uiState.afterSignUpRedirectUrl,
    });

    routingService.replace(getPath(Path.profileSetupSso));
};

/**
 * loadSsoStep2Sequence is an async action that processes the second stage of an SSO login, handling OAuth responses,
 * updating authentication states, and redirecting the user based on login results and post-action requirements.
 */
export const loadSsoStep2Sequence = (
    options: LoadSsoStep2SequenceOptions,
    setSsoData: (data: SsoData | null) => void
) => {
    return async (dispatch: Dispatch, getState: () => State) => {
        let response;

        try {
            response = await requestOAuthLogin(options);
        } catch (err) {
            handleAuthError(err, options);

            return;
        }

        const data = response.data || {};
        const signUpCode = data.UserTransientToken;
        const email = data.UserEmailAddress;
        const firstName = data.UserFirstName;
        const lastName = data.UserLastName;
        const uiState = data.UiState ? JSON.parse(decodeURIComponent(data.UiState)) : {};

        if (!signUpCode) {
            await handleLoggedInUser(uiState, options, dispatch, getState);

            return;
        }

        handleNotLoggedInUser(setSsoData, options.oauthProvider, uiState, {
            firstName,
            lastName,
            email,
            signUpCode,
        });
    };
};
